import { cn } from '@core/utils/cn';

/**
 * Material Icons ligature wrapper.
 *
 * Deliberately keeps the ligature-font approach rather than swapping to a React icon
 * package: 108 distinct icon names are used across the Angular templates, so
 * `<mat-icon>delete_outline</mat-icon>` → `<Icon>delete_outline</Icon>` survives
 * find-and-replace with no name-mapping table to maintain.
 *
 * Sizes reflect the effective values after styles.scss's override block:
 * icon-button icons are 18px (line 1694), section/header icons 20px (line 1672),
 * empty-state icons 36px (line 1681).
 */
const SIZE = {
  xs: 'text-[16px]',
  sm: 'text-[18px]',
  md: 'text-[20px]',
  lg: 'text-[24px]',
  xl: 'text-[36px]',
} as const;

export type IconSize = keyof typeof SIZE;

interface IconProps {
  /** The ligature name, e.g. `delete_outline`. */
  children: string;
  size?: IconSize;
  className?: string;
  /**
   * Icons are decorative by default (`aria-hidden`). Set a label only when the icon
   * is the sole content of an interactive element with no other accessible name.
   */
  label?: string;
}

export function Icon({ children, size = 'sm', className, label }: IconProps) {
  return (
    <span
      className={cn('material-icons', SIZE[size], className)}
      aria-hidden={label ? undefined : true}
      role={label ? 'img' : undefined}
      aria-label={label}
    >
      {children}
    </span>
  );
}
